import { useMemo } from "react";

// Define types for better type safety
type AmountInput = number | string | null | undefined;

interface FormattedAmount {
  integerPart: string; // renamed from shownAmount
  decimalPart: string; // renamed from showDecimalPart
  isKFormatted: boolean; // renamed from showKFormatting
  fullFormattedAmount: string;
}

// K-formatting function (replaces the dependency on utils/common)
const formatNumberInK = (value: number): string => {
  if (value >= 10000000) {
    // 1 crore = 10,000,000
    return `${(value / 10000000).toFixed(1)}Cr`;
  } else if (value >= 100000) {
    // 1 lakh = 100,000
    return `${(value / 100000).toFixed(1)}L`;
  } else if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}K`;
  }
  return value.toString();
};

// Helper function to format the amount
const formatAmount = (
  amount: AmountInput,
  showDecimals: boolean = true,
  kFormatting: boolean = false
): FormattedAmount => {
  // Handle null, undefined, or 0 cases
  if (amount === null || amount === undefined || amount === 0) {
    return {
      integerPart: "0.",
      decimalPart: "00",
      isKFormatted: false,
      fullFormattedAmount: "0.00",
    };
  }

  // Parse string input to number
  let numericAmount: number;
  if (typeof amount === "string") {
    numericAmount = parseFloat(amount.replaceAll(",", ""));
  } else {
    numericAmount = amount;
  }

  // Handle NaN case
  if (isNaN(numericAmount)) {
    return {
      integerPart: "0.",
      decimalPart: "00",
      isKFormatted: false,
      fullFormattedAmount: "0.00",
    };
  }

  // Round to two decimal places
  const roundedAmount = Math.floor(numericAmount * 100) / 100;
  const amountString = roundedAmount.toFixed(2);
  const [integerPart, decimalPart = "00"] = amountString.split(".");

  // Convert to number for formatting
  const intValue = parseInt(integerPart, 10);

  // Determine if K formatting should be applied
  const isKFormatted = kFormatting && intValue >= 1000;

  // Formatted integer part (with or without K formatting)
  const formattedIntegerPart = isKFormatted
    ? formatNumberInK(intValue)
    : intValue.toLocaleString("en-IN");

  // If K formatting is applied, don't show decimals
  const finalShowDecimals = showDecimals && !isKFormatted;
  const displayDecimalPart = finalShowDecimals ? decimalPart : "00";

  // Construct the integer part with period
  const integerWithDot = isKFormatted
    ? `${formattedIntegerPart}`
    : `${formattedIntegerPart}.`;

  // Create full formatted amount for easy consumption
  const fullFormattedAmount = `${formattedIntegerPart}.${displayDecimalPart}`;

  return {
    integerPart: integerWithDot,
    decimalPart: displayDecimalPart,
    isKFormatted,
    fullFormattedAmount,
  };
};

// Improved hook using useMemo for performance
export const useFormatAmount = (
  amount: AmountInput,
  showDecimals: boolean = true,
  kFormatting: boolean = false
): FormattedAmount => {
  // Use useMemo to avoid recalculating unless dependencies change
  return useMemo(
    () => formatAmount(amount, showDecimals, kFormatting),
    [amount, showDecimals, kFormatting]
  );
};
